import styles from './alert.module.css';

import attentionIcon from 'assets/svg/window_attention-01.svg';
import successIcon from 'assets/svg/window_ok-01.svg';
import sureIcon from 'assets/svg/window_sure-01.svg';
import infoIcon from 'assets/svg/window_info-01.svg';

import RegularButton from 'ui/RegularButton';
import Modal from '../../ui/Modal';
import PropTypes from 'prop-types';

const STATUS_IMAGES = {
  success: successIcon,
  warning: attentionIcon,
  error: attentionIcon,
  info: infoIcon,
  sure: sureIcon,
};

const TITLES = {
  success: 'Информация',
  warning: 'Предупреждение',
  error: 'Ошибка',
  info: 'Информация',
  sure: 'Подтверждение',
};

/**
 * **Alert** — это компонент модального окна, отображающий сообщения разных уровней важности
 * (информация, предупреждение, ошибка, подтверждение).
 * Компонент поддерживает пользовательские обработчики для закрытия и подтверждения действий,
 * а также изменяет дизайн в зависимости от переданного статуса.
 *
 * ### Описание параметров:
 *
 * @param {boolean} open - Флаг, указывающий, открыто ли модальное окно.
 * @param {'success'|'warning'|'error'|'info'|'sure'} status - Статус сообщения.
 *    Допустимые значения:
 *    - `'success'` — Успех (Информация).
 *    - `'warning'` — Предупреждение.
 *    - `'error'` — Ошибка.
 *    - `'info'` — Информация.
 *    - `'sure'` — Подтверждение действия.
 *    В случае некорректного значения будет выдано предупреждение в консоли.
 * @param {string} message - Текст сообщения, отображаемый в центральной части окна.
 * @param {function} onClose - Обработчик, вызываемый при нажатии на кнопку закрытия окна.
 * @param {function} [onSubmit] - Обработчик, вызываемый при подтверждении действия (только для статуса `'sure'`).
 *
 * ### Статусы и кнопки:
 * - **success, info**: кнопка "Продолжить" с основным цветом.
 * - **warning**: кнопка "Продолжить" с акцентным цветом.
 * - **error**: кнопка "Продолжить" с цветом ошибки.
 * - **sure**: две кнопки "Отмена" (вторичный цвет) и "Подтвердить" (цвет ошибки).
 *
 * ### Пример использования:
 * ```jsx
 * import Alert from './Alert';
 *
 * <Alert
 *   open={true}
 *   status="warning"
 *   message="Вы уверены, что хотите продолжить?"
 *   onClose={() => console.log('Модальное окно закрыто')}
 *   onSubmit={() => console.log('Действие подтверждено')}
 * />
 * ```
 */
const Alert = ({ open, status, message, onClose, onSubmit = undefined }) => {
  const SuccessButtons = () => (
    <RegularButton text="Продолжить" color="primary" variant="flat" onClick={() => onClose()} />
  );

  const WarningButtons = () => (
    <RegularButton
      text="Продолжить"
      color="accent"
      variant="flat"
      onClick={() => {
        onClose();
        onSubmit && onSubmit();
      }}
    />
  );

  const ErrorButtons = () => (
    <RegularButton text="Продолжить" color="accent" variant="flat" onClick={() => onClose()} />
  );

  const InfoButtons = () => (
    <RegularButton text="Продолжить" color="primary" variant="flat" onClick={() => onClose()} />
  );

  const SureButtons = () => (
    <>
      <RegularButton text="Отмена" color="secondary" variant="outlined" onClick={() => onClose()} />
      <RegularButton
        text="Подтвердить"
        color="error"
        variant="flat"
        onClick={() => {
          onClose();
          onSubmit && onSubmit();
        }}
      />
    </>
  );

  return (
    <Modal title={TITLES[status]} open={open} onClose={onClose} hideDefaultFooter>
      <div className={styles.alertBody}>
        <img className={styles.statusImg} src={STATUS_IMAGES[status]} alt="status-image" />
        <div className={styles.content}>
          <div>{message}</div>
        </div>
      </div>
      <Modal.Footer>
        {status === 'success' && <SuccessButtons />}
        {status === 'warning' && <WarningButtons />}
        {status === 'error' && <ErrorButtons />}
        {status === 'info' && <InfoButtons />}
        {status === 'sure' && <SureButtons />}
      </Modal.Footer>
    </Modal>
  );
};

Alert.propTypes = {
  /**
   * Флаг, указывающий, открыто ли модальное окно.
   * Значение: `true` для отображения окна, `false` для скрытия.
   */
  open: PropTypes.bool.isRequired,

  /**
   * Статус сообщения, определяющий уровень важности и тип отображаемого контента.
   * Допустимые значения:
   * - `'success'` — успешное сообщение, передает информацию.
   * - `'warning'` — предупреждающее сообщение.
   * - `'error'` — сообщение об ошибке.
   * - `'info'` — информационное сообщение.
   * - `'sure'` — запрос подтверждения действия.
   */
  status: PropTypes.oneOf(['success', 'warning', 'error', 'info', 'sure']).isRequired,

  /**
   * Текст сообщения, отображаемый в модальном окне.
   * Например, "Вы уверены, что хотите продолжить?"
   */
  message: PropTypes.string.isRequired,

  /**
   * Функция, вызываемая при закрытии модального окна.
   * Выполняется при нажатии на кнопку "Отмена" или крестик.
   */
  onClose: PropTypes.func.isRequired,

  /**
   * [Необязательно] Функция, вызываемая при подтверждении действия.
   * Используется только для статусов `'warning'` и `'sure'`.
   */
  onSubmit: PropTypes.func,
};

export default Alert;
